import { useEffect, useMemo, useState, lazy, Suspense } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Calendar, Compass, Map as MapIcon, Plus, Search, Bed, Utensils, Navigation, Trash2, X, Menu } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { ACCOMMODATIONS, TASTING_STOPS } from './lib/data';
import { FAQ } from './lib/faq';
import { SCOPE } from './lib/brandScope';
import { REGION_HUBS, STATE_HUBS, getRegionHubForLocation, getStateHref } from './lib/trailHubs';
import { cn } from './lib/utils';
import { routeDistanceMi } from './utils/geo';
import { bookingSearchUrl } from './utils/booking';
import type { Accommodation, ExperienceType, ItineraryItem, OliveVarietal, TastingStop, USState } from './types';

const MapView = lazy(() => import('./components/MapView'));

type AppTab = 'map' | 'trails' | 'stops' | 'stays' | 'itinerary';

const TAB_TO_PATH: Record<AppTab, string> = {
  map: '/',
  trails: '/trails',
  stops: '/stops',
  stays: '/stays',
  itinerary: '/plan',
};

const PATH_TO_TAB: Record<string, AppTab> = {
  '/': 'map',
  '/map': 'map',
  '/trails': 'trails',
  '/stops': 'stops',
  '/stays': 'stays',
  '/plan': 'itinerary',
};

const EXPERIENCE_TYPES: ExperienceType[] = [
  'Guided Tasting',
  'Orchard Tour',
  'Retail Sampling',
  'Sommelier Class',
  'Farm Pickup',
];

const OLIVE_VARIETALS: OliveVarietal[] = ['Arbequina', 'Arbosana', 'Koroneiki', 'Picual', 'Coratina', 'Blend'];

const STATE_LABEL: Record<USState, string> = { TX: 'Texas', NM: 'New Mexico', AZ: 'Arizona' };

function MapSkeleton({ label = 'Loading map...' }: { label?: string }) {
  return (
    <div className="w-full h-full flex items-center justify-center bg-sidebar/50">
      <div className="flex flex-col items-center gap-3 text-ink-light">
        <div className="w-8 h-8 border-2 border-border border-t-accent rounded-full animate-spin" />
        <span className="text-[10px] font-bold uppercase tracking-widest">{label}</span>
      </div>
    </div>
  );
}

function CardImage({ src, alt }: { src?: string; alt: string }) {
  if (!src) {
    return (
      <div className="w-full h-full bg-[radial-gradient(circle_at_top_left,_rgba(85,107,47,0.18),_transparent_45%),linear-gradient(135deg,_rgba(230,224,210,0.95),_rgba(245,241,232,0.75))] flex items-end p-6">
        <span className="text-[10px] uppercase tracking-widest text-ink-light font-bold">Image pending</span>
      </div>
    );
  }
  return <img src={src} alt={alt} className="w-full h-full object-cover" referrerPolicy="no-referrer" />;
}

export default function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const activeTab = PATH_TO_TAB[location.pathname] ?? 'map';
  const setActiveTab = (tab: AppTab) => {
    navigate(TAB_TO_PATH[tab]);
    setMobileMenuOpen(false);
  };

  const [selectedStop, setSelectedStop] = useState<TastingStop | null>(null);
  const [selectedStay, setSelectedStay] = useState<Accommodation | null>(null);
  const [itinerary, setItinerary] = useState<ItineraryItem[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [experienceFilter, setExperienceFilter] = useState<ExperienceType | ''>('');
  const [varietalFilter, setVarietalFilter] = useState<OliveVarietal | ''>('');
  const [stateFilter, setStateFilter] = useState<USState | ''>('');
  const [regionFilter, setRegionFilter] = useState<string>('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const nextState = params.get('state');
    const nextRegion = params.get('region');
    setStateFilter(nextState === 'TX' || nextState === 'NM' || nextState === 'AZ' ? nextState : '');
    setRegionFilter(nextRegion ?? '');
  }, [location.search]);

  const searchMatches = (value: string) => !searchQuery || value.toLowerCase().includes(searchQuery.toLowerCase());

  const filteredStops = useMemo(() => {
    return TASTING_STOPS.filter((stop) => {
      const region = getRegionHubForLocation(stop.id);
      const matchesSearch = searchMatches(
        `${stop.name} ${stop.description} ${stop.city} ${stop.highlights?.join(' ') ?? ''} ${stop.varietals.join(' ')} ${stop.experiences.join(' ')} ${region?.title ?? ''}`,
      );
      const matchesExperience = experienceFilter ? stop.experiences.includes(experienceFilter) : true;
      const matchesVarietal = varietalFilter ? stop.varietals.includes(varietalFilter) : true;
      const matchesState = stateFilter ? stop.state === stateFilter : true;
      const matchesRegion = regionFilter ? stop.region === regionFilter : true;
      return matchesSearch && matchesExperience && matchesVarietal && matchesState && matchesRegion;
    });
  }, [experienceFilter, varietalFilter, stateFilter, regionFilter, searchQuery]);

  const filteredStays = useMemo(() => {
    return ACCOMMODATIONS.filter((stay) => {
      const region = getRegionHubForLocation(stay.id);
      const matchesSearch = searchMatches(
        `${stay.name} ${stay.description} ${stay.city} ${stay.amenities.join(' ')} ${stay.style} ${region?.title ?? ''}`,
      );
      const matchesState = stateFilter ? stay.state === stateFilter : true;
      const matchesRegion = regionFilter ? stay.region === regionFilter : true;
      return matchesSearch && matchesState && matchesRegion;
    });
  }, [regionFilter, searchQuery, stateFilter]);

  const itineraryStops = useMemo(() => {
    return itinerary
      .map((item) => {
        const locationItem = [...TASTING_STOPS, ...ACCOMMODATIONS].find((entry) => entry.id === item.locationId);
        return locationItem ? { item, location: locationItem } : null;
      })
      .filter((entry): entry is NonNullable<typeof entry> => entry !== null);
  }, [itinerary]);

  const routeStats = useMemo(() => {
    const points = itineraryStops.map((entry) => ({ lat: entry.location.lat, lng: entry.location.lng }));
    const miles = routeDistanceMi(points);
    const drivingMiles = miles * 1.28;
    const hours = drivingMiles / 42;
    return { miles, drivingMiles, hours };
  }, [itineraryStops]);

  const addToItinerary = (id: string) => {
    if (itinerary.some((item) => item.locationId === id)) return;
    setItinerary((current) => [...current, { id: `${id}-${current.length}`, locationId: id, order: current.length }]);
  };

  const removeFromItinerary = (id: string) => {
    setItinerary((current) => current.filter((item) => item.id !== id).map((item, index) => ({ ...item, order: index })));
  };

  const setItineraryNote = (id: string, notes: string) => {
    setItinerary((current) => current.map((item) => (item.id === id ? { ...item, notes } : item)));
  };

  const clearAllFilters = () => {
    setSearchQuery('');
    setExperienceFilter('');
    setVarietalFilter('');
    setStateFilter('');
    setRegionFilter('');
  };

  const openTab = (tab: AppTab) => {
    clearAllFilters();
    navigate(TAB_TO_PATH[tab]);
  };

  const openRegionTab = (tab: AppTab, state: USState, region: string) => {
    const params = new URLSearchParams();
    params.set('state', state);
    params.set('region', region);
    navigate({ pathname: TAB_TO_PATH[tab], search: `?${params.toString()}` });
  };

  const exportGpx = () => {
    if (!itineraryStops.length) return;
    const escape = (value: string) =>
      value.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('"', '&quot;');
    const gpx = `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="Olive Oil Trails" xmlns="http://www.topografix.com/GPX/1/1">
  ${itineraryStops
    .map(
      ({ location }) => `<wpt lat="${location.lat}" lon="${location.lng}">
    <name>${escape(location.name)}</name>
    <desc>${escape(`${location.city}, ${location.state}`)}</desc>
  </wpt>`,
    )
    .join('\n')}
</gpx>`;
    const blob = new Blob([gpx], { type: 'application/gpx+xml' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `olive-atlas-route-${new Date().toISOString().slice(0, 10)}.gpx`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-bg text-ink">
      <header className="sticky top-0 z-40 border-b border-border bg-white/80 backdrop-blur-sm">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-4 space-y-4">
          <div className="flex items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-accent text-white flex items-center justify-center">
                <Compass size={17} />
              </div>
              <div className="leading-tight">
                <div className="font-serif text-lg">Olive Oil Trails</div>
                <div className="text-[10px] uppercase tracking-widest text-ink-light">Southwest tasting weekends</div>
              </div>
            </Link>

            <nav className="hidden md:flex items-center gap-2">
              {([
                ['map', 'Route map', MapIcon],
                ['trails', 'Regions', Compass],
                ['stops', 'Stops', Utensils],
                ['stays', 'Stays', Bed],
                ['itinerary', 'Itinerary', Calendar],
              ] as const).map(([id, label, Icon]) => (
                <button
                  key={id}
                  onClick={() => setActiveTab(id as AppTab)}
                  className={cn('pill-minimal', activeTab === id && 'active')}
                >
                  <Icon size={12} />
                  {label}
                </button>
              ))}
            </nav>

            <button
              onClick={() => setMobileMenuOpen(true)}
              className="md:hidden p-2 text-ink hover:bg-sidebar rounded-lg transition-colors"
              aria-label="Open menu"
            >
              <Menu size={24} />
            </button>
          </div>

          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div className="text-[10px] uppercase tracking-widest font-bold text-ink-light">State chapters</div>
            <nav className="flex flex-wrap gap-2">
              {STATE_HUBS.map((hub) => (
                <Link
                  key={hub.state}
                  to={getStateHref(hub.state)}
                  className="pill-minimal"
                >
                  {STATE_LABEL[hub.state]}
                </Link>
              ))}
            </nav>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileMenuOpen(false)}
              className="fixed inset-0 bg-ink/60 backdrop-blur-sm z-[90] md:hidden"
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed right-0 top-0 bottom-0 w-[280px] bg-bg border-l border-border z-[100] md:hidden flex flex-col"
            >
              <div className="flex items-center justify-between p-4 border-b border-border">
                <span className="font-serif text-lg">Menu</span>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-2 text-ink hover:bg-sidebar rounded-lg transition-colors"
                  aria-label="Close menu"
                >
                  <X size={24} />
                </button>
              </div>
              <nav className="flex-1 p-4 space-y-2">
                {([
                  ['map', 'Route map', MapIcon],
                  ['trails', 'Regions', Compass],
                  ['stops', 'Stops', Utensils],
                  ['stays', 'Stays', Bed],
                  ['itinerary', 'Itinerary', Calendar],
                ] as const).map(([id, label, Icon]) => (
                  <button
                    key={id}
                    onClick={() => setActiveTab(id as AppTab)}
                    className={cn(
                      'w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-colors',
                      activeTab === id
                        ? 'bg-ink text-white'
                        : 'text-ink hover:bg-sidebar'
                    )}
                  >
                    <Icon size={20} />
                    <span className="font-medium">{label}</span>
                  </button>
                ))}
              </nav>
              <div className="p-4 border-t border-border space-y-2">
                <p className="text-[10px] uppercase tracking-widest font-bold text-ink-light mb-3">State chapters</p>
                {STATE_HUBS.map((hub) => (
                  <Link
                    key={hub.state}
                    to={getStateHref(hub.state)}
                    onClick={() => setMobileMenuOpen(false)}
                    className="block px-4 py-2 text-sm text-ink-light hover:text-ink hover:bg-sidebar rounded-lg transition-colors"
                  >
                    {hub.state === 'TX' ? 'Texas' : hub.state === 'NM' ? 'New Mexico' : 'Arizona'}
                  </Link>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <main>
        <AnimatePresence mode="wait">
          {activeTab === 'map' ? (
            <motion.section key="map" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-12 md:py-16 space-y-10">
                <div className="grid grid-cols-1 xl:grid-cols-[1.05fr_0.95fr] gap-10 items-start">
                  <div className="space-y-6">
                    <span className="pill-minimal active">{SCOPE.label}</span>
                    <h1 className="text-5xl md:text-7xl font-serif text-ink tracking-tight leading-[1.02]">
                      Southwest olive oil tasting, <span className="italic font-light text-accent">mill tours, and trip planning.</span>
                    </h1>
                    <p className="text-lg text-ink-light leading-relaxed max-w-2xl">
                      {SCOPE.heroTagline}
                    </p>
                    <div className="flex flex-wrap gap-3">
                      <Link to="/olive-oil-tasting-dripping-springs/" className="btn-minimal">Dripping Springs tasting</Link>
                      <Link to="/olive-mill-tours-southwest/" className="btn-minimal">Olive mill tours</Link>
                      <button onClick={() => openTab('trails')} className="btn-minimal">Browse route regions</button>
                      <Link to={getStateHref('TX')} className="btn-minimal">Start with Texas</Link>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-2">
                      <button onClick={() => openTab('stops')} className="card-minimal p-4 bg-white/80 text-left transition-transform hover:-translate-y-0.5">
                        <p className="text-[10px] uppercase tracking-widest text-ink-light font-bold">Stops</p>
                        <p className="text-3xl font-serif text-accent">{TASTING_STOPS.length}</p>
                      </button>
                      <button onClick={() => openTab('stays')} className="card-minimal p-4 bg-white/80 text-left transition-transform hover:-translate-y-0.5">
                        <p className="text-[10px] uppercase tracking-widest text-ink-light font-bold">Stay bases</p>
                        <p className="text-3xl font-serif text-accent">{ACCOMMODATIONS.length}</p>
                      </button>
                      <button onClick={() => openTab('trails')} className="card-minimal p-4 bg-white/80 text-left transition-transform hover:-translate-y-0.5">
                        <p className="text-[10px] uppercase tracking-widest text-ink-light font-bold">Regions</p>
                        <p className="text-3xl font-serif text-accent">{REGION_HUBS.length}</p>
                      </button>
                      <button onClick={() => openTab('trails')} className="card-minimal p-4 bg-white/80 text-left transition-transform hover:-translate-y-0.5">
                        <p className="text-[10px] uppercase tracking-widest text-ink-light font-bold">States</p>
                        <p className="text-2xl font-serif text-ink">{STATE_HUBS.length}</p>
                      </button>
                    </div>
                  </div>

                  <div className="h-[520px] rounded-[32px] overflow-hidden border border-border bg-sidebar/50">
                    <Suspense fallback={<MapSkeleton />}>
                      <MapView
                        stops={TASTING_STOPS}
                        accommodations={ACCOMMODATIONS}
                        fitBounds
                        onAddToItinerary={addToItinerary}
                        itineraryIds={itinerary.map((item) => item.locationId)}
                        onSelect={(id, kind) => {
                          if (kind === 'tasting-stop') {
                            setSelectedStop(TASTING_STOPS.find((stop) => stop.id === id) ?? null);
                          } else {
                            setSelectedStay(ACCOMMODATIONS.find((stay) => stay.id === id) ?? null);
                          }
                        }}
                      />
                    </Suspense>
                  </div>
                </div>

                <section className="bg-white/80 border border-border rounded-[32px] p-6 md:p-8 space-y-5">
                  <div className="space-y-2">
                    <span className="pill-minimal active">Start here</span>
                    <h2 className="text-3xl md:text-4xl font-serif tracking-tight">Plan the olive trip by intent.</h2>
                    <p className="text-sm md:text-base text-ink-light leading-relaxed max-w-3xl">
                      Use these planning pages when you already know the kind of trip you want: a Dripping Springs tasting stop, a mill-tour style experience, or a state-level tasting weekend.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
                    {[
                      ['Dripping Springs tasting', '/olive-oil-tasting-dripping-springs/', 'Texas Hill Country tasting notes, stay bases, and what to confirm before you go.'],
                      ['Southwest mill tours', '/olive-mill-tours-southwest/', 'Compare mill visits, orchard stops, retail tastings, and tour expectations.'],
                      ['Texas olive tasting', '/texas-olive-oil-tasting/', 'The state guide for Dripping Springs, Gruene, and Central Texas detours.'],
                      ['Arizona olive tasting', '/arizona-olive-oil-tasting/', 'Queen Creek, Phoenix-area tasting, and Scottsdale stay planning.'],
                      ['New Mexico olive tasting', '/new-mexico-olive-oil-tasting/', 'Santa Fe and Albuquerque tasting rooms folded into a food-first weekend.'],
                    ].map(([label, href, note]) => (
                      <Link key={href} to={href} className="card-minimal p-5 bg-sidebar/60 space-y-3 block hover:border-accent">
                        <p className="text-[10px] uppercase tracking-widest text-accent font-bold italic">Planning page</p>
                        <h3 className="text-2xl font-serif tracking-tight">{label}</h3>
                        <p className="text-sm text-ink-light leading-relaxed">{note}</p>
                      </Link>
                    ))}
                  </div>
                </section>

                <section className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {REGION_HUBS.map((region) => (
                    <article key={region.key} className="card-minimal p-6 bg-white/80 space-y-4">
                      <div className="space-y-2">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-accent italic">Route region</p>
                        <h2 className="text-3xl font-serif tracking-tight">{region.shortTitle}</h2>
                      </div>
                      <p className="text-sm text-ink-light leading-relaxed">{region.subtitle}</p>
                      <div className="flex flex-wrap gap-2">
                        <span className="pill-minimal active">{region.stopIds.length} stops</span>
                        <span className="pill-minimal">{region.stayIds.length} stays</span>
                      </div>
                      <div className="flex flex-wrap gap-3 pt-2">
                        <button onClick={() => openRegionTab('stops', region.state, region.key)} className="btn-minimal">
                          Explore stops
                        </button>
                        <button onClick={() => openRegionTab('stays', region.state, region.key)} className="btn-minimal">
                          Find stays
                        </button>
                      </div>
                    </article>
                  ))}
                </section>

                <section className="max-w-4xl space-y-5">
                  <span className="pill-minimal active">Why this route</span>
                  <h2 className="text-4xl md:text-5xl font-serif tracking-tight">
                    Smaller routes, <span className="italic font-light text-accent">better weekends.</span>
                  </h2>
                  <p className="text-lg text-ink-light leading-relaxed">
                    This guide stays selective on purpose. The Southwest has more olive producers and pantry shops than
                    you see here, but the best weekends come from a shorter list of places that are easy to visit,
                    pleasant to pair together, and actually worth the detour.
                  </p>
                </section>

                <section className="bg-white/80 border border-border rounded-[32px] p-6 md:p-8 space-y-5">
                  <div className="space-y-2">
                    <span className="pill-minimal active">Before you taste</span>
                    <h2 className="text-3xl md:text-4xl font-serif tracking-tight">
                      The 3-Bottle Rule, <span className="italic font-light text-accent">a simple way to taste.</span>
                    </h2>
                    <p className="text-base md:text-lg text-ink-light leading-relaxed max-w-2xl">
                      Skip the overwhelm. Here's a tested way to compare olive oils without palate fatigue.
                    </p>
                  </div>
                  <div className="relative overflow-hidden rounded-xl border border-border bg-sidebar/30 max-w-2xl mx-auto cursor-pointer group" onClick={() => window.open('/tasting-guide.png', '_blank')}>
                    <img
                      src="/tasting-guide.png"
                      alt="The 3-Bottle Rule: How to taste olive oil without palate fatigue. Start with delicate oils, move to medium intensity, finish with bold flavors. Use bread or apple slices between tastings."
                      className="w-full h-auto max-h-[320px] md:max-h-[400px] object-cover object-top transition-transform group-hover:scale-[1.02]"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-ink/0 group-hover:bg-ink/5 transition-colors flex items-center justify-center">
                      <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 px-3 py-1.5 rounded-full text-xs font-semibold shadow-sm">
                        Click to view full size
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 justify-center">
                    <span className="pill-minimal">Delicate → Medium → Bold</span>
                    <span className="pill-minimal">Reset with bread or apple</span>
                  </div>
                </section>

                <section className="rounded-[28px] border border-border bg-[linear-gradient(135deg,rgba(255,255,255,0.92),rgba(245,241,232,0.88))] p-6 md:p-7">
                  <div className="max-w-4xl space-y-3">
                    <p className="text-[10px] uppercase tracking-widest font-bold text-accent">Travel note</p>
                    <p className="text-sm md:text-base text-ink-light leading-relaxed">
                      Tasting fees, store hours, class schedules, harvest timing, and visitor access can change. Check the official venue page before you go, especially if you are driving to an orchard, booking a class, or building the weekend around one specific stop.
                    </p>
                  </div>
                </section>

                <section className="grid grid-cols-1 xl:grid-cols-[1.05fr_0.95fr] gap-10 items-start">
                  <div className="space-y-5 max-w-3xl">
                    <span className="pill-minimal active">Along the route</span>
                    <h2 className="text-4xl md:text-5xl font-serif tracking-tight">
                      One olive route, <span className="italic font-light text-accent">three very different moods.</span>
                    </h2>
                    <p className="text-lg text-ink-light leading-relaxed">
                      The trip starts in Texas, where olive tasting feels most rooted in orchards, long lunches, and
                      Hill Country weekends. Dripping Springs is the polished anchor, Gruene adds an easy river-town
                      detour, and the wider Central Texas stops make sense for travelers who want a little more producer
                      depth beyond the obvious first weekend.
                    </p>
                    <p className="text-lg text-ink-light leading-relaxed">
                      From there, the route turns west. Santa Fe and Albuquerque offer a more urban tasting style built
                      around walkable counters, pantry shopping, and shorter stops between other parts of a Southwest trip.
                      Arizona finishes differently again, with Queen Creek delivering the most complete mill visit in the
                      guide and Phoenix-area tasting rooms giving travelers a softer landing if they want to pair olive
                      tastings with a hotel weekend.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 gap-4">
                    {STATE_HUBS.map((hub) => (
                      <Link key={hub.state} to={getStateHref(hub.state)} className="card-minimal p-6 bg-white/80 space-y-3 block transition-transform hover:-translate-y-0.5">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-accent italic">{STATE_LABEL[hub.state]}</p>
                        <h3 className="text-2xl font-serif tracking-tight">{hub.title}</h3>
                        <p className="text-sm text-ink-light leading-relaxed">{hub.subtitle}</p>
                      </Link>
                    ))}
                  </div>
                </section>
              </div>
            </motion.section>
          ) : null}

          {activeTab === 'trails' ? (
            <motion.section key="trails" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="max-w-screen-2xl mx-auto px-6 md:px-12 py-12 md:py-16 space-y-10">
              <div className="space-y-3">
                <h2 className="text-5xl font-serif tracking-tight">Route <span className="italic font-light text-accent">regions</span></h2>
                <p className="text-sm text-ink-light max-w-2xl">Each region is a real driveable cluster rather than a county dump.</p>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {REGION_HUBS.map((region) => (
                  <article key={region.key} className="card-minimal bg-white/80 p-8 space-y-5">
                    <div className="space-y-2">
                      <span className="text-[10px] font-bold uppercase tracking-widest text-accent italic">Route region</span>
                      <h3 className="text-3xl font-serif tracking-tight">{region.title}</h3>
                    </div>
                    <p className="text-sm text-ink-light leading-relaxed">{region.subtitle}</p>
                    <p className="text-sm italic text-ink-light">{region.mood}</p>
                    <div className="flex flex-wrap gap-2 pt-2">
                      <span className="pill-minimal active">{region.stopIds.length} tasting stops</span>
                      <span className="pill-minimal">{region.stayIds.length} stays</span>
                    </div>
                    <button
                      onClick={() => {
                        openRegionTab('stops', region.state, region.key);
                      }}
                      className="btn-minimal"
                    >
                      Explore region stops
                    </button>
                  </article>
                ))}
              </div>
            </motion.section>
          ) : null}

          {activeTab === 'stops' ? (
            <motion.section key="stops" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="max-w-screen-2xl mx-auto px-6 md:px-12 py-12 md:py-16 space-y-10">
              <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
                <div className="space-y-3">
                  <h2 className="text-5xl font-serif tracking-tight">Tasting <span className="italic font-light text-accent">stops</span></h2>
                  <p className="text-sm text-ink-light max-w-2xl">Olive-oil stops across {SCOPE.title}, from polished guided tastings to quieter orchard detours.</p>
                </div>
                <div className="space-y-3">
                  <div className="flex bg-sidebar rounded-full border border-border p-1 items-center">
                    <Search className="ml-3 text-ink-light" size={14} />
                    <input
                      type="text"
                      placeholder="Search towns, orchards, experiences..."
                      value={searchQuery}
                      onChange={(event) => setSearchQuery(event.target.value)}
                      className="bg-transparent px-3 py-1 text-xs focus:outline-none placeholder:text-ink-light/50 w-64"
                    />
                  </div>
                  <div className="flex flex-wrap gap-2 max-w-3xl justify-start lg:justify-end">
                    <button onClick={clearAllFilters} className={cn('pill-minimal', !regionFilter && !stateFilter && !experienceFilter && !varietalFilter && !searchQuery && 'active')}>Reset</button>
                    {STATE_HUBS.map((hub) => (
                      <button
                        key={hub.state}
                        onClick={() => setStateFilter(stateFilter === hub.state ? '' : hub.state)}
                        className={cn('pill-minimal', stateFilter === hub.state && 'active')}
                      >
                        {STATE_LABEL[hub.state]}
                      </button>
                    ))}
                    {REGION_HUBS.map((region) => (
                      <button key={region.key} onClick={() => setRegionFilter(regionFilter === region.key ? '' : region.key)} className={cn('pill-minimal', regionFilter === region.key && 'active')}>
                        {region.shortTitle}
                      </button>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-2 max-w-3xl justify-start lg:justify-end">
                    {EXPERIENCE_TYPES.map((experience) => (
                      <button key={experience} onClick={() => setExperienceFilter(experienceFilter === experience ? '' : experience)} className={cn('pill-minimal', experienceFilter === experience && 'active')}>
                        {experience}
                      </button>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-2 max-w-3xl justify-start lg:justify-end">
                    {OLIVE_VARIETALS.map((varietal) => (
                      <button key={varietal} onClick={() => setVarietalFilter(varietalFilter === varietal ? '' : varietal)} className={cn('pill-minimal', varietalFilter === varietal && 'active')}>
                        {varietal}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
                {filteredStops.map((stop) => (
                  <article key={stop.id} className="card-minimal bg-white/80 overflow-hidden flex flex-col">
                    <div className="relative aspect-[3/2] overflow-hidden">
                      <CardImage src={stop.image} alt={stop.name} />
                      <div className="absolute top-4 left-4">
                        <span className="bg-white/90 backdrop-blur px-3 py-1 rounded-full text-[9px] font-bold uppercase tracking-widest border border-border">{stop.costRange}</span>
                      </div>
                    </div>
                    <div className="p-8 space-y-4 flex-1 flex flex-col">
                      <div className="space-y-1">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-accent italic">
                          {stop.city}, {STATE_LABEL[stop.state]} · {getRegionHubForLocation(stop.id)?.shortTitle}
                        </span>
                        <h3 className="text-2xl font-serif tracking-tight">{stop.name}</h3>
                      </div>
                      <p className="text-sm text-ink-light line-clamp-3 leading-relaxed italic">{stop.description}</p>
                      <div className="flex flex-wrap gap-2 mt-auto">
                        {stop.experiences.map((experience) => (
                          <span key={experience} className="px-3 py-1 bg-sidebar text-ink-light text-[9px] font-bold uppercase tracking-widest rounded-full">
                            {experience}
                          </span>
                        ))}
                      </div>
                      <div className="flex items-center gap-3 pt-4">
                        <button onClick={() => setSelectedStop(stop)} className="flex-1 btn-minimal">Stop details</button>
                        <button onClick={() => addToItinerary(stop.id)} className="p-3 border border-border rounded-full text-ink hover:bg-ink hover:text-white transition-all disabled:opacity-20" disabled={itinerary.some((item) => item.locationId === stop.id)}>
                          <Plus size={16} />
                        </button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </motion.section>
          ) : null}

          {activeTab === 'stays' ? (
            <motion.section key="stays" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="max-w-screen-2xl mx-auto px-6 md:px-12 py-12 md:py-16 space-y-10">
              <div className="space-y-3">
                <h2 className="text-5xl font-serif tracking-tight">Where to <span className="italic font-light text-accent">stay</span></h2>
                <p className="text-sm text-ink-light max-w-2xl">Hotels and retreats that make the route easier to drive, from resort weekends to lower-key overnight bases.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
                {filteredStays.map((stay) => (
                  <article key={stay.id} className="card-minimal bg-white/80 overflow-hidden flex flex-col">
                    <div className="relative aspect-[3/2] overflow-hidden">
                      <CardImage src={stay.image} alt={stay.name} />
                      <div className="absolute top-4 left-4">
                        <span className="bg-white/90 backdrop-blur px-3 py-1 rounded-full text-[9px] font-bold uppercase tracking-widest border border-border">{stay.priceRange}</span>
                      </div>
                    </div>
                    <div className="p-8 space-y-4 flex-1 flex flex-col">
                      <div className="space-y-1">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-accent italic">
                          {stay.city}, {STATE_LABEL[stay.state]} · {stay.style}
                        </span>
                        <h3 className="text-2xl font-serif tracking-tight">{stay.name}</h3>
                      </div>
                      <p className="text-sm text-ink-light line-clamp-3 leading-relaxed italic">{stay.description}</p>
                      <div className="flex items-center gap-3 pt-4 mt-auto">
                        <button onClick={() => setSelectedStay(stay)} className="flex-1 btn-minimal">Stay details</button>
                        <button onClick={() => addToItinerary(stay.id)} className="p-3 border border-border rounded-full text-ink hover:bg-ink hover:text-white transition-all disabled:opacity-20" disabled={itinerary.some((item) => item.locationId === stay.id)}>
                          <Plus size={16} />
                        </button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </motion.section>
          ) : null}

          {activeTab === 'itinerary' ? (
            <motion.section key="itinerary" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="max-w-screen-2xl mx-auto px-6 md:px-12 py-12 md:py-16 grid grid-cols-1 xl:grid-cols-3 gap-10">
              <div className="xl:col-span-2 space-y-8">
                <div className="space-y-3">
                  <h2 className="text-5xl font-serif tracking-tight">Your <span className="italic font-light text-accent">route</span></h2>
                  <p className="text-sm text-ink-light">A simple sequence of tasting stops and stay bases.</p>
                </div>

                {itineraryStops.length === 0 ? (
                  <div className="text-center py-24 border border-dashed border-border bg-sidebar/30 rounded-[32px] space-y-6">
                    <Calendar size={48} className="mx-auto text-border" />
                    <p className="text-ink font-serif text-2xl italic">Your route is empty.</p>
                    <button onClick={() => setActiveTab('stops')} className="btn-minimal">Browse tasting stops</button>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {itineraryStops.map(({ item, location: place }, index) => (
                      <article key={item.id} className="card-minimal bg-white/80 p-6 flex flex-col md:flex-row gap-6">
                        <div className="w-full md:w-28 h-28 rounded-2xl overflow-hidden shrink-0">
                          <CardImage src={place.image} alt={place.name} />
                        </div>
                        <div className="flex-1 space-y-3">
                          <div className="flex items-center justify-between gap-4">
                            <div>
                              <p className="text-[10px] uppercase tracking-widest text-accent font-bold">
                                Stop {index + 1} · {place.type === 'tasting-stop' ? 'Tasting stop' : 'Stay'}
                              </p>
                              <h3 className="text-2xl font-serif tracking-tight">{place.name}</h3>
                            </div>
                            <button onClick={() => removeFromItinerary(item.id)} className="p-2 text-ink-light hover:text-red-500 transition-colors">
                              <Trash2 size={16} />
                            </button>
                          </div>
                          <div className="text-sm text-ink-light flex flex-wrap gap-x-4 gap-y-1">
                            <span>{place.city}, {place.state}</span>
                            <span className="inline-flex items-center gap-1"><Navigation size={12} />{place.address}</span>
                          </div>
                          <textarea
                            value={item.notes ?? ''}
                            onChange={(event) => setItineraryNote(item.id, event.target.value)}
                            placeholder="Add reservation timing, route note, or tasting reminder..."
                            rows={2}
                            className="w-full text-xs bg-sidebar/50 border border-border rounded-lg px-3 py-2 text-ink placeholder:text-ink-light/60 focus:outline-none focus:border-accent resize-y"
                          />
                        </div>
                      </article>
                    ))}
                  </div>
                )}
              </div>

              <aside className="space-y-8">
                <div className="bg-sidebar p-8 rounded-[32px] border border-border space-y-6">
                  <h3 className="text-2xl font-serif tracking-tight">Trip context</h3>
                  <div className="grid gap-4">
                    <div className="p-4 bg-white rounded-2xl border border-border space-y-2">
                      <p className="text-[10px] uppercase tracking-widest text-ink-light font-bold">Total stops</p>
                      <p className="text-3xl font-serif text-accent">{itineraryStops.length}</p>
                    </div>
                    <div className="p-4 bg-white rounded-2xl border border-border space-y-2">
                      <p className="text-[10px] uppercase tracking-widest text-ink-light font-bold">Approx. driving miles</p>
                      <p className="text-3xl font-serif text-accent">{routeStats.drivingMiles.toFixed(0)} mi</p>
                    </div>
                    <div className="p-4 bg-white rounded-2xl border border-border space-y-2">
                      <p className="text-[10px] uppercase tracking-widest text-ink-light font-bold">Approx. drive time</p>
                      <p className="text-3xl font-serif text-accent">{routeStats.hours.toFixed(1)} hr</p>
                    </div>
                  </div>
                  <button onClick={exportGpx} className="w-full btn-minimal bg-ink text-white py-4 !rounded-2xl">Export GPX route</button>
                </div>
                <div className="aspect-square rounded-[32px] overflow-hidden border border-border bg-sidebar/50">
                  <Suspense fallback={<MapSkeleton label="Route preview..." />}>
                    <MapView
                      points={itineraryStops.map(({ location: place }) => ({
                        id: place.id,
                        name: place.name,
                        city: place.city,
                        state: place.state,
                        lat: place.lat,
                        lng: place.lng,
                        kind: place.type,
                        subtitle: place.type === 'tasting-stop' ? place.experiences.join(' • ') : `${place.style} • ${place.priceRange}`,
                      }))}
                      fitBounds
                    />
                  </Suspense>
                </div>
              </aside>
            </motion.section>
          ) : null}
        </AnimatePresence>

        <section className="max-w-screen-2xl mx-auto px-6 md:px-12 py-8 md:py-12 space-y-6">
          <div className="max-w-4xl space-y-3">
            <span className="pill-minimal active">FAQ</span>
            <h2 className="text-4xl md:text-5xl font-serif tracking-tight">Planning notes for the <span className="italic font-light text-accent">olive route.</span></h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {FAQ.map((item) => (
              <details key={item.question} className="card-minimal bg-white/80 p-6">
                <summary className="font-serif text-xl cursor-pointer">{item.question}</summary>
                <p className="mt-4 text-sm text-ink-light leading-relaxed">{item.answer}</p>
              </details>
            ))}
          </div>
        </section>

        <footer className="mt-16 py-4 px-6 md:px-12 border-t border-border bg-sidebar/30">
          <div className="max-w-screen-2xl mx-auto flex flex-wrap items-center gap-x-5 gap-y-2 text-[10px] text-ink-light">
            <span className="font-serif italic text-accent font-bold tracking-tighter text-[11px]">Olive Oil Trails</span>
            <a href="/privacy.html" className="uppercase tracking-widest font-bold hover:text-accent transition-colors">Privacy</a>
            <a href="/terms.html" className="uppercase tracking-widest font-bold hover:text-accent transition-colors">Terms</a>
            <a href="/disclosure.html" className="uppercase tracking-widest font-bold hover:text-accent transition-colors">Disclosure</a>
            <span className="ml-auto text-ink-light/70">© {new Date().getFullYear()} · Hotel booking links may earn a commission</span>
          </div>
        </footer>
      </main>

      <AnimatePresence>
        {selectedStop ? (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-ink/90 backdrop-blur-md" onClick={() => setSelectedStop(null)} />
            <motion.div initial={{ opacity: 0, scale: 0.98, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.98, y: 10 }} className="bg-bg w-full max-w-5xl max-h-[94vh] overflow-y-auto relative z-10 rounded-[32px] border border-border shadow-2xl flex flex-col lg:flex-row">
              <button onClick={() => setSelectedStop(null)} className="absolute top-6 right-6 p-2 text-ink-light hover:bg-sidebar rounded-full transition-colors z-30 bg-white/80 backdrop-blur-sm border border-border">
                <X size={20} />
              </button>
              <div className="lg:w-2/5 h-80 lg:h-auto relative overflow-hidden border-r border-border">
                <CardImage src={selectedStop.image} alt={selectedStop.name} />
              </div>
              <div className="flex-1 p-8 lg:p-16 space-y-8">
                <div className="space-y-3">
                  <span className="pill-minimal active">Tasting stop</span>
                  <h2 className="text-5xl font-serif tracking-tight">{selectedStop.name}</h2>
                  <p className="text-lg text-ink-light italic">{selectedStop.city}, {STATE_LABEL[selectedStop.state]}</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                  <div className="space-y-5">
                    <div>
                      <h4 className="text-[10px] font-bold uppercase tracking-widest text-accent mb-2">Why it matters</h4>
                      <p className="text-ink-light leading-relaxed">{selectedStop.story}</p>
                    </div>
                    <div>
                      <h4 className="text-[10px] font-bold uppercase tracking-widest text-accent mb-2">Pairing notes</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedStop.pairingNotes.map((note) => (
                          <span key={note} className="pill-minimal">{note}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="bg-sidebar/50 p-8 rounded-2xl border border-border space-y-5">
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-ink-light">Hours</p>
                      <p className="text-sm text-ink">{selectedStop.hours}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-ink-light">Visitor policy</p>
                      <p className="text-sm text-ink">{selectedStop.visitorPolicy}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-ink-light">Varietals</p>
                      <p className="text-sm text-ink">{selectedStop.varietals.join(', ')}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-ink-light">Address</p>
                      <p className="text-sm text-ink">{selectedStop.address}</p>
                    </div>
                    <div className="grid gap-3 pt-2">
                      <button onClick={() => addToItinerary(selectedStop.id)} className="w-full btn-minimal bg-ink text-white py-4 !rounded-xl" disabled={itinerary.some((item) => item.locationId === selectedStop.id)}>
                        {itinerary.some((item) => item.locationId === selectedStop.id) ? 'Already in route' : 'Add to itinerary'}
                      </button>
                      <a href={selectedStop.website} target="_blank" rel="noreferrer noopener" className="w-full btn-minimal text-center">Open official site</a>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        ) : null}
      </AnimatePresence>

      <AnimatePresence>
        {selectedStay ? (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-ink/90 backdrop-blur-md" onClick={() => setSelectedStay(null)} />
            <motion.div initial={{ opacity: 0, scale: 0.98, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.98, y: 10 }} className="bg-bg w-full max-w-5xl max-h-[94vh] overflow-y-auto relative z-10 rounded-[32px] border border-border shadow-2xl flex flex-col lg:flex-row">
              <button onClick={() => setSelectedStay(null)} className="absolute top-6 right-6 p-2 text-ink-light hover:bg-sidebar rounded-full transition-colors z-30 bg-white/80 backdrop-blur-sm border border-border">
                <X size={20} />
              </button>
              <div className="lg:w-2/5 h-80 lg:h-auto relative overflow-hidden border-r border-border">
                <CardImage src={selectedStay.image} alt={selectedStay.name} />
              </div>
              <div className="flex-1 p-8 lg:p-16 space-y-8">
                <div className="space-y-3">
                  <span className="pill-minimal active">Stay base</span>
                  <h2 className="text-5xl font-serif tracking-tight">{selectedStay.name}</h2>
                  <p className="text-lg text-ink-light italic">{selectedStay.city}, {STATE_LABEL[selectedStay.state]} · {selectedStay.style}</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                  <div className="space-y-5">
                    <div>
                      <h4 className="text-[10px] font-bold uppercase tracking-widest text-accent mb-2">Why it fits the route</h4>
                      <p className="text-ink-light leading-relaxed">{selectedStay.description}</p>
                    </div>
                    <div>
                      <h4 className="text-[10px] font-bold uppercase tracking-widest text-accent mb-2">Amenities</h4>
                      <div className="grid gap-2">
                        {selectedStay.amenities.map((amenity) => (
                          <div key={amenity} className="text-sm text-ink-light">• {amenity}</div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="bg-sidebar/50 p-8 rounded-2xl border border-border space-y-5">
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-ink-light">Price tier</p>
                      <p className="text-sm text-ink">{selectedStay.priceRange}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-ink-light">Pet policy</p>
                      <p className="text-sm text-ink">{selectedStay.petFriendly ? 'Pet-friendly' : 'No pets listed'}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-ink-light">Address</p>
                      <p className="text-sm text-ink">{selectedStay.address}</p>
                    </div>
                    <div className="grid gap-3 pt-2">
                      <button onClick={() => addToItinerary(selectedStay.id)} className="w-full btn-minimal bg-ink text-white py-4 !rounded-xl" disabled={itinerary.some((item) => item.locationId === selectedStay.id)}>
                        {itinerary.some((item) => item.locationId === selectedStay.id) ? 'Already in route' : 'Add stay to itinerary'}
                      </button>
                      <a href={bookingSearchUrl(selectedStay)} target="_blank" rel="noreferrer noopener sponsored" data-stay22="book" className="w-full btn-minimal text-center bg-accent text-white border-accent hover:bg-ink hover:border-ink">
                        Find best rates
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
